import { useState, useEffect } from 'react'

export function useUserCity(fallback: string = 'A') {
  const [city, setCity] = useState<string>('')
  const [countryCode, setCountryCode] = useState<string>('U.S.')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchWithTimeout = (
      url: string,
      ms: number = 3000,
    ): Promise<Response> => {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), ms)
      return fetch(url, { signal: controller.signal }).finally(() =>
        clearTimeout(timeout),
      )
    }

    const formatCountry = (code: string): string => {
      const countryMap: Record<string, string> = {
        US: 'U.S.',
        GB: 'U.K.',
        UK: 'U.K.',
        CA: 'CANADA',
        AU: 'AUSTRALIA',
        NZ: 'NEW ZEALAND',
        IE: 'IRELAND',
        DE: 'GERMANY',
        FR: 'FRANCE',
        ES: 'SPAIN',
        IT: 'ITALY',
        NL: 'NETHERLANDS',
        BE: 'BELGIUM',
        AT: 'AUSTRIA',
        CH: 'SWITZERLAND',
        SE: 'SWEDEN',
        NO: 'NORWAY',
        DK: 'DENMARK',
        FI: 'FINLAND',
        PT: 'PORTUGAL',
        GR: 'GREECE',
        PL: 'POLAND',
        CZ: 'CZECH REPUBLIC',
        RO: 'ROMANIA',
        HU: 'HUNGARY',
        HR: 'CROATIA',
        BG: 'BULGARIA',
        SK: 'SLOVAKIA',
        SI: 'SLOVENIA',
        LT: 'LITHUANIA',
        LV: 'LATVIA',
        EE: 'ESTONIA',
        CY: 'CYPRUS',
        MT: 'MALTA',
        LU: 'LUXEMBOURG',
        IN: 'INDIA',
        JP: 'JAPAN',
        KR: 'SOUTH KOREA',
        SG: 'SINGAPORE',
        MY: 'MALAYSIA',
        PH: 'PHILIPPINES',
        TH: 'THAILAND',
        ID: 'INDONESIA',
        VN: 'VIETNAM',
        TW: 'TAIWAN',
        HK: 'HONG KONG',
        BR: 'BRAZIL',
        MX: 'MEXICO',
        AR: 'ARGENTINA',
        CL: 'CHILE',
        CO: 'COLOMBIA',
        PE: 'PERU',
        ZA: 'SOUTH AFRICA',
        NG: 'NIGERIA',
        KE: 'KENYA',
        EG: 'EGYPT',
        IL: 'ISRAEL',
        AE: 'UAE',
        SA: 'SAUDI ARABIA',
        TR: 'TURKEY',
        PK: 'PAKISTAN',
        BD: 'BANGLADESH',
      }
      const upper = code.toUpperCase()
      return countryMap[upper] || upper
    }

    const fetchCity = async () => {
      try {
        const response = await fetchWithTimeout('https://ipapi.co/json/')
        if (!response.ok) throw new Error('Primary API failed')
        const data = await response.json()
        if (data.city) {
          setCity(data.city)
          if (data.country_code) {
            setCountryCode(formatCountry(data.country_code))
          }
          setLoading(false)
          return
        }
        throw new Error('No city in response')
      } catch {
        try {
          const response = await fetchWithTimeout('https://ipinfo.io/json')
          if (!response.ok) throw new Error('Fallback API failed')
          const data = await response.json()
          if (data.city) {
            setCity(data.city)
            if (data.country) {
              setCountryCode(formatCountry(data.country))
            }
            setLoading(false)
            return
          }
          throw new Error('No city in response')
        } catch {
          setCity(fallback)
          setLoading(false)
        }
      }
    }

    fetchCity()
  }, [fallback])

  return { city, countryCode, loading }
}
